import { useEffect } from 'react';

import { MyProfileResponseDto, UseQueryOptionsType } from '@repo/services/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import http from 'services/http';
import { setUserIdState } from 'store/userIdStore';
import { checkIsLogin } from 'utils/auth';
import { LOADING_KEY, withLoading } from 'utils/promise';

export const MY_PROFILE_KEY: string[] = ['myProfile'];

export function getMyProfile() {
  return http.get<MyProfileResponseDto>(`/my/profile`, {}, { useRedirectAndLogout: false, useLoading: false });
}

export const useGetMyProfile = () => {
  const isCustomLogin = checkIsLogin();

  const query = useQuery({
    queryKey: MY_PROFILE_KEY,
    queryFn: () => withLoading(LOADING_KEY.PROFILES, getMyProfile),
    enabled: isCustomLogin,
    refetchOnWindowFocus: true, // 이메일 인증 후에 재호출되도록
  });

  useEffect(() => {
    if (query.data?.id) {
      setUserIdState(query.data.id);
    }
  }, [query.data?.id]);

  return query;
};

export interface RevenueShareTermsAgreementResponseDto {
  revenueShareTermsAgreedAt: string;
}

// 수익분배약관 동의 (멱등). 201 응답의 동의 일시를 프로필 캐시에 반영해 게이트 즉시 해제.
export const usePostRevenueShareTermsAgreement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      http.post<RevenueShareTermsAgreementResponseDto>(
        '/my/revenue-share-term-agreements',
        {},
        { useErrorToast: false }
      ),
    throwOnError: false,
    onSuccess: (data) => {
      queryClient.setQueryData<MyProfileResponseDto>(MY_PROFILE_KEY, (old) =>
        old ? { ...old, revenueShareTermsAgreedAt: data.revenueShareTermsAgreedAt } : old
      );
    },
  });
};

export interface NsfwResponseDto {
  adultVerifiedAt: string;
  enableNsfw: boolean;
  isAdult: boolean;
}

export const useGetEnableNsfw = (options: Omit<UseQueryOptionsType<any>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: ['nsfw'],
    queryFn: () => http.get<NsfwResponseDto>(`/my/enable-nsfw`, {}, { useLoading: false }),
    staleTime: 1000 * 60 * 1,
    gcTime: 1000 * 60 * 1,
    ...options,
  });
};

export const useUpdateNsfwEnable = (userId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => http.put(`/users/${userId}/enable-nsfw/enable`, {}, { useLoading: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nsfw'] });
      // queryClient.invalidateQueries(weeklyBannerKeys.openList({ enableNsfw: true }));
    },
  });
};

export const useUpdateNsfwDisable = (userId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => http.put(`/users/${userId}/enable-nsfw/disable`, {}, { useLoading: false }),
    throwOnError: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nsfw'] });
      // queryClient.invalidateQueries(weeklyBannerKeys.openList({ enableNsfw: false }));
    },
  });
};
